/**
 * Incident WebSocket — mirrors the kanban board hook structure
 * (jittered exponential backoff, on-open subscribe, 45s ping, seq-gap detection).
 *
 * Endpoint: `GET /api/notes/incidents/ws/incident/{id}` (Cloudflare Access JWT auth on upgrade).
 */

import { useEffect, useRef, useCallback } from 'react';
import { KANBAN_API_BASE_URL } from '../services/kanbanApi';
import type { IncidentWsEnvelope } from '../types/incidentRealtime';

function httpBaseToWsBase(httpUrl: string): string {
  if (httpUrl.startsWith('https://')) return `wss://${httpUrl.slice('https://'.length)}`;
  if (httpUrl.startsWith('http://')) return `ws://${httpUrl.slice('http://'.length)}`;
  return httpUrl;
}

const WS_BASE = httpBaseToWsBase(KANBAN_API_BASE_URL.replace(/\/$/, ''));

function withJitter(ms: number): number {
  return ms + Math.floor(Math.random() * Math.min(2000, ms * 0.2));
}

function parseFrames(raw: string): IncidentWsEnvelope[] {
  const trimmed = raw.trim();
  if (!trimmed) return [];
  const lines = trimmed.includes('\n') ? trimmed.split('\n') : [trimmed];
  const out: IncidentWsEnvelope[] = [];
  for (const line of lines) {
    const s = line.trim();
    if (!s) continue;
    try {
      out.push(JSON.parse(s) as IncidentWsEnvelope);
    } catch {
      console.warn('Incident WS: skipped non-JSON frame', s.slice(0, 120));
    }
  }
  return out;
}

export function incidentWsUrl(incidentId: string): string {
  return `${WS_BASE}/incidents/ws/incident/${encodeURIComponent(incidentId)}`;
}

export interface UseIncidentWebSocketOptions {
  incidentId: string | undefined;
  /** When false, no socket (e.g. caller has no read permission yet). */
  enabled: boolean;
  onEnvelope: (env: IncidentWsEnvelope) => void;
  /** Fired when a `seq` gap is detected; caller should refetch canonical state. */
  onSeqGap?: () => void;
}

/**
 * Connects to `incident:{id}` and dispatches every envelope through `onEnvelope`.
 *
 * `system.connected` / `system.subscribed` / `system.pong` / `system.error` frames
 * pass through as-is. Domain frames advance the local `seq` counter and fire
 * `onSeqGap` when a gap is detected.
 */
export function useIncidentWebSocket(options: UseIncidentWebSocketOptions): void {
  const { incidentId, enabled, onEnvelope, onSeqGap } = options;
  const onEnvelopeRef = useRef(onEnvelope);
  const onSeqGapRef = useRef(onSeqGap);
  useEffect(() => {
    onEnvelopeRef.current = onEnvelope;
  }, [onEnvelope]);
  useEffect(() => {
    onSeqGapRef.current = onSeqGap;
  }, [onSeqGap]);

  const lastSeqRef = useRef<number | null>(null);
  const reconnectAttemptRef = useRef(0);
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pingTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const clearTimers = useCallback(() => {
    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
    if (pingTimerRef.current) {
      clearInterval(pingTimerRef.current);
      pingTimerRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (!incidentId || !enabled) {
      clearTimers();
      lastSeqRef.current = null;
      wsRef.current?.close();
      wsRef.current = null;
      return;
    }

    let cancelled = false;

    const scheduleReconnect = () => {
      if (cancelled) return;
      const attempt = reconnectAttemptRef.current++;
      const base = Math.min(60_000, 1000 * 2 ** Math.min(attempt, 16));
      const delay = withJitter(base);
      clearTimers();
      reconnectTimerRef.current = setTimeout(() => {
        reconnectTimerRef.current = null;
        connect();
      }, delay);
    };

    const connect = () => {
      if (cancelled) return;
      clearTimers();
      lastSeqRef.current = null;
      try {
        const url = incidentWsUrl(incidentId);
        const ws = new WebSocket(url);
        wsRef.current = ws;

        ws.onopen = () => {
          if (cancelled) return;
          reconnectAttemptRef.current = 0;
          try {
            ws.send(
              JSON.stringify({
                action: 'subscribe',
                channels: [`incident:${incidentId}`],
              }),
            );
          } catch {
            /* optional */
          }
          pingTimerRef.current = setInterval(() => {
            try {
              if (ws.readyState === WebSocket.OPEN) {
                ws.send(JSON.stringify({ action: 'ping', nonce: String(Date.now()) }));
              }
            } catch {
              /* ignore */
            }
          }, 45_000);
        };

        ws.onmessage = (ev) => {
          if (cancelled) return;
          const text = typeof ev.data === 'string' ? ev.data : '';
          const envs = parseFrames(text);
          for (const env of envs) {
            if (env.type === 'system.connected' || env.type === 'system.subscribed') {
              lastSeqRef.current =
                typeof env.seq === 'number' ? env.seq : lastSeqRef.current;
              onEnvelopeRef.current(env);
              continue;
            }
            if (env.type === 'system.pong' || env.type === 'system.error') {
              onEnvelopeRef.current(env);
              continue;
            }

            const seq = env.seq;
            if (typeof seq === 'number') {
              const last = lastSeqRef.current;
              if (last !== null && seq !== last + 1) {
                onSeqGapRef.current?.();
                lastSeqRef.current = null;
              }
              lastSeqRef.current = seq;
            }
            onEnvelopeRef.current(env);
          }
        };

        ws.onerror = () => {
          ws.close();
        };

        ws.onclose = () => {
          wsRef.current = null;
          clearTimers();
          if (!cancelled) scheduleReconnect();
        };
      } catch {
        scheduleReconnect();
      }
    };

    connect();

    return () => {
      cancelled = true;
      clearTimers();
      reconnectAttemptRef.current = 0;
      lastSeqRef.current = null;
      wsRef.current?.close();
      wsRef.current = null;
    };
  }, [incidentId, enabled, clearTimers]);
}
